// frontend/source/portal/components/MediumManagerModal.js
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import adminApi from "../../services/adminapi";
import ConfirmDialog from "./ConfirmDialog";
import "./MediumManagerModal.css";

const MediumManagerModal = ({ onClose, onMediumsUpdated }) => {
  const [mediums, setMediums] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [newMediumName, setNewMediumName] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    medium: null,
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadMediums();
  }, []);

  const loadMediums = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getMediums();
      setMediums(data);
      setError(null);
    } catch (err) {
      setError("Failed to load mediums");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (medium) => {
    setEditingId(medium._id);
    setEditingName(medium.name);
    setIsCreating(false);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  const handleSaveEdit = async (mediumId) => {
    if (editingName.trim() === "") {
      alert("Medium name cannot be empty");
      return;
    }

    try {
      setIsSaving(true);
      const updated = await adminApi.updateMedium(mediumId, {
        name: editingName.trim(),
      });
      setMediums(mediums.map((m) => (m._id === mediumId ? updated : m)));
      setEditingId(null);
      setEditingName("");
      onMediumsUpdated?.();
    } catch (err) {
      setError("Failed to update medium");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartCreate = () => {
    setIsCreating(true);
    setEditingId(null);
    setNewMediumName("");
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewMediumName("");
  };

  const handleSaveCreate = async () => {
    if (newMediumName.trim() === "") {
      alert("Medium name cannot be empty");
      return;
    }

    try {
      setIsSaving(true);
      const created = await adminApi.createMedium({
        name: newMediumName.trim(),
      });
      setMediums([created, ...mediums]);
      setIsCreating(false);
      setNewMediumName("");
      onMediumsUpdated?.();
    } catch (err) {
      setError("Failed to create medium");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (medium) => {
    setDeleteConfirm({ isOpen: true, medium });
  };

  const confirmDelete = async () => {
    try {
      setIsSaving(true);
      await adminApi.deleteMedium(deleteConfirm.medium._id);
      setMediums(mediums.filter((m) => m._id !== deleteConfirm.medium._id));
      setDeleteConfirm({ isOpen: false, medium: null });
      onMediumsUpdated?.();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete medium");
      console.error(err);
      setDeleteConfirm({ isOpen: false, medium: null });
    } finally {
      setIsSaving(false);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ isOpen: false, medium: null });
  };

  const handleKeyPress = (e, action) => {
    if (e.key === "Enter") {
      action();
    } else if (e.key === "Escape") {
      if (isCreating) handleCancelCreate();
      if (editingId) handleCancelEdit();
    }
  };

  const modalContent = (
    <div className="medium-manager-overlay" onClick={onClose}>
      <div
        className="medium-manager-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="medium-manager-header">
          <h2 className="medium-manager-title">Manage Mediums</h2>
          <button className="medium-manager-close" onClick={onClose}>
            ×
          </button>
        </div>

        {error && <div className="medium-manager-error">{error}</div>}

        <div className="medium-manager-content">
          {/* Create New Button */}
          <div className="medium-manager-create-section">
            {!isCreating ? (
              <button
                onClick={handleStartCreate}
                className="medium-manager-btn create-new-btn"
                disabled={loading || isSaving}
              >
                + Add New Medium
              </button>
            ) : (
              <div className="medium-manager-create-form">
                <input
                  type="text"
                  value={newMediumName}
                  onChange={(e) => setNewMediumName(e.target.value)}
                  onKeyDown={(e) => handleKeyPress(e, handleSaveCreate)}
                  className="medium-manager-input"
                  placeholder="Enter medium name"
                  autoFocus
                  disabled={isSaving}
                />
                <div className="medium-manager-create-actions">
                  <button
                    onClick={handleSaveCreate}
                    className="medium-manager-btn save-btn"
                    disabled={isSaving || !newMediumName.trim()}
                  >
                    {isSaving ? "Creating..." : "Create"}
                  </button>
                  <button
                    onClick={handleCancelCreate}
                    className="medium-manager-btn cancel-btn"
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mediums List */}
          <div className="medium-manager-list">
            {loading ? (
              <div className="medium-manager-loading">Loading mediums...</div>
            ) : mediums.length === 0 ? (
              <div className="medium-manager-empty">
                No mediums found. Create one to get started.
              </div>
            ) : (
              mediums.map((medium) => (
                <div key={medium._id} className="medium-manager-item">
                  {editingId === medium._id ? (
                    <div className="medium-manager-edit-form">
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) =>
                          handleKeyPress(e, () => handleSaveEdit(medium._id))
                        }
                        className="medium-manager-input"
                        autoFocus
                        disabled={isSaving}
                      />
                      <div className="medium-manager-item-actions">
                        <button
                          onClick={() => handleSaveEdit(medium._id)}
                          className="medium-manager-btn save-btn"
                          disabled={isSaving}
                        >
                          {isSaving ? "Saving..." : "Save"}
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="medium-manager-btn cancel-btn"
                          disabled={isSaving}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <span className="medium-manager-item-name">
                        {medium.name}
                      </span>
                      <div className="medium-manager-item-actions">
                        <button
                          onClick={() => handleStartEdit(medium)}
                          className="medium-manager-action-btn edit-btn"
                          disabled={isSaving}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(medium)}
                          className="medium-manager-action-btn delete-btn"
                          disabled={isSaving}
                        >
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="medium-manager-footer">
          <button onClick={onClose} className="medium-manager-btn close-btn">
            Close
          </button>
        </div>

        <ConfirmDialog
          isOpen={deleteConfirm.isOpen}
          title="Delete Medium?"
          message={`Are you sure you want to delete "${deleteConfirm.medium?.name}"? Artworks using this medium will have their medium field cleared.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default MediumManagerModal;
